import SerialWrapper from '../serialWrapper.jsx'
import API from './api.jsx'
import Lock from '../lock.jsx'

class FiscalDeviceAPI {
  constructor (options = {}) {
    this.wrapper = new SerialWrapper(options)
    this.lock = new Lock()
    this.api = new API(this.wrapper, this.lock)
  }

  connect () {
    return this.wrapper.open()
  }

  disconnect (config) {
    return this.wrapper.close(config)
  }

  isSerialPortOpen () {
    return this.wrapper.portOpened
  }

  async getSavedSerialPort () {
    return await this.wrapper.getSavedSerialPort()
  }

  async openSerialPort () {
    if (!this.isSerialPortOpen()) {
      await this.connect()
    }
  }

  async closeSerialPort () {
    if (this.isSerialPortOpen()) {
      await this.disconnect()
    }
  }

  async play () {
    try {
      await this.openSerialPort()

      const res = await this.api.playMusic()

      await this.closeSerialPort()

      return {
        success: true,
        data: res
      }
    } catch (error) {
      // console.error('Error playing music:', error)
      // throw new Error('execution_error')

      return {
        success: false,
        data: error
      }
    }
  }

  async createSale (order) {
    if (!order) {
      throw new Error('Order is required')
    }

    try {
      await this.openSerialPort()

      const res = await this.api.createSale(order)

      await this.closeSerialPort()

      return {
        success: true,
        data: res
      }
    } catch (error) {
      // console.error('Error creating sale:', error)
      // throw new Error('execution_error')

      return {
        success: false,
        data: error
      }
    }
  }

  async getDiagnosticInfo () {
    try {
      await this.openSerialPort()

      const res = await this.api.getDiagnosticInfo()

      await this.closeSerialPort()

      return {
        success: true,
        data: res
      }
    } catch (error) {
      // console.error('Error getting diagnostic info:', error)
      // throw new Error('execution_error')
      return {
        success: false,
        data: error
      }
    }
  }

  async runDailyFinancialReport (closure = true) {
    try {
      await this.openSerialPort()

      const res = await this.api.runDailyFinancialReport(closure)

      await this.closeSerialPort()

      return {
        success: true,
        data: res
      }
    } catch (error) {
      // console.error('Error running daily financial report:', error)
      // throw new Error('execution_error')
      return {
        success: false,
        data: error
      }
    }
  }

  async getStatus () {
    try {
      await this.openSerialPort()

      const res = await this.api.getStatus()

      await this.closeSerialPort()

      return {
        success: true,
        data: res
      }
    } catch (error) {
      // console.error('Error running get status:', error)
      // throw new Error('execution_error')
      return {
        success: false,
        data: error
      }
    }
  }

  async moneyInOut (amount) {
    try {
      await this.openSerialPort()

      const res = await this.api.moneyInOut(`${amount}`)

      await this.closeSerialPort()

      return {
        success: true,
        data: res
      }
    } catch (error) {
      // console.error('Error running money in/out:', error)
      // throw new Error('execution_error')
      return {
        success: false,
        data: error
      }
    }
  }

  async addTextDisplay (text1, text2 = null) {
    try {
      await this.openSerialPort()

      await this.api.clearDisplay()

      let res = null

      if (text1 && text2) {
        await this.api.addTextUpperLineDisplay(text1)
        res = await this.api.addTextLowerLineDisplay(text2)
      }

      if (text1) {
        res = await this.api.addTextUpperLineDisplay(text1)
      }

      if (text2) {
        res = await this.api.addTextLowerLineDisplay(text2)
      }

      await this.closeSerialPort()

      return {
        success: true,
        data: res
      }
    } catch (error) {
      // console.error('Error running add text to display:', error)
      // throw new Error('execution_error')
      return {
        success: false,
        data: error
      }
    }
  }

  async setDate (date) {
    try {
      await this.openSerialPort()

      const res = await this.api.setDate(`${date}`)

      await this.closeSerialPort()

      return {
        success: true,
        data: res
      }
    } catch (error) {
      // console.error('Error running set date:', error)
      // throw new Error('execution_error')
      return {
        success: false,
        data: error
      }
    }
  }

  async getDate () {
    try {
      await this.openSerialPort()

      const res = await this.api.getDate()

      await this.closeSerialPort()

      return {
        success: true,
        data: res
      }
    } catch (error) {
      // console.error('Error running get date:', error)
      // throw new Error('execution_error')
      return {
        success: false,
        data: error
      }
    }
  }

  async runShortFinancialReportByDate (start, end = null) {
    try {
      await this.openSerialPort()

      const res = await this.api.runShortFinancialReportByDate(start, end)

      await this.closeSerialPort()

      return {
        success: true,
        data: res
      }
    } catch (error) {
      // console.error('Error running short financial report by date:', error)
      // throw new Error('execution_error')
      return {
        success: false,
        data: error
      }
    }
  }

  async runExtendedFinancialReportByDate (start, end = null) {
    try {
      await this.openSerialPort()

      const res = await this.api.runExtendedFinancialReportByDate(start, end)

      await this.closeSerialPort()

      return {
        success: true,
        data: res
      }
    } catch (error) {
      // console.error('Error running extended financial report by date:', error)
      // throw new Error('execution_error')
      return {
        success: false,
        data: error
      }
    }
  }

  async checkIfHasUnfinishedFiscalOperationAndAbortIt () {
    try {
      await this.openSerialPort()

      const res = await this.api.checkIfHasUnfinishedFiscalOperationAndAbortIt()

      await this.closeSerialPort()

      return {
        success: true,
        data: res
      }
    } catch (error) {
      // console.error('Error running unblock fiscal printer:', error)
      // throw new Error('execution_error')
      return {
        success: false,
        data: error
      }
    }
  }

  async getLastFiscalRecord () {
    try {
      await this.openSerialPort()

      const res = await this.api.getLastFiscalRecord()

      await this.closeSerialPort()

      return {
        success: true,
        data: res
      }
    } catch (error) {
      // console.error('Error running get last fiscal record:', error)
      // throw new Error('execution_error')
      return {
        success: false,
        data: error
      }
    }
  }

  async movePaper () {
    try {
      await this.openSerialPort()

      const res = await this.api.movePaper()

      await this.closeSerialPort()

      return {
        success: true,
        data: res
      }
    } catch (error) {
      // console.error('Error running move paper:', error)
      // throw new Error('execution_error')
      return {
        success: false,
        data: error
      }
    }
  }

  async cutPaper () {
    try {
      await this.openSerialPort()

      const res = await this.api.cutPaper()

      await this.closeSerialPort()

      return {
        success: true,
        data: res
      }
    } catch (error) {
      // console.error('Error running cut paper:', error)
      // throw new Error('execution_error')
      return {
        success: false,
        data: error
      }
    }
  }
}

export default FiscalDeviceAPI
